
import { StoryConfig, ChildProfile, GeneratedBook, CharacterBible } from "../types";
import { debugLog } from "../utils/debugLogger";
import { buildIdentityLockHeader, debugLogCharacterBible, assertIdentityHeaderPresent } from "./characterBibleService";

// Local definitions of Gemini API constants
const Type = {
  OBJECT: 'OBJECT',
  STRING: 'STRING',
  ARRAY: 'ARRAY',
  INTEGER: 'INTEGER',
} as const;

const Modality = {
  AUDIO: 'AUDIO',
} as const;

const AVAILABLE_VOICES = ['Kore', 'Puck', 'Charon', 'Fenrir', 'Zephyr'];

async function callGeminiProxy(payload: any) {
  const response = await fetch('/api/gemini/proxy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  const rawText = await response.text();
  let data: any = {};
  try {
    data = rawText ? JSON.parse(rawText) : {};
  } catch (e) {
    throw new Error(`Gemini proxy invalid JSON (${response.status})`);
  }

  if (!response.ok) {
    throw new Error(data.error || `Magic forge encountered a problem (${response.status}).`);
  }

  const textContent = data.text || data.candidates?.[0]?.content?.parts?.[0]?.text;

  return {
    ...data,
    text: textContent
  };
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

async function retryOperation<T>(operation: () => Promise<T>, retries = 3, backoff = 1000): Promise<T> {
  try {
    return await operation();
  } catch (error: any) {
    const msg = error.message || '';
    const isRetryable = msg.includes('429') || msg.includes('quota') || msg.includes('500') || msg.includes('503');
    if (retries > 0 && isRetryable) {
      debugLog("RETRY", "Retry Operation", { msg, retriesLeft: retries });
      await delay(backoff + Math.random() * 500);
      return retryOperation(operation, retries - 1, backoff * 1.5);
    }
    throw error;
  }
}

/**
 * Calculates page count based on industry standards for age groups.
 */
function calculatePageCount(audience: string, lengthPref: string): number {
  const isToddler = audience.includes('Toddler') || audience.includes('2-4');
  const isLittleKid = audience.includes('Little') || audience.includes('5-7');

  // Base ranges
  let min = 10;
  let max = 12;

  if (isToddler) {
    min = 6; max = 8;
  } else if (isLittleKid) {
    min = 8; max = 10;
  }

  if (lengthPref === 'Short') return min;
  if (lengthPref === 'Long') return max;
  return Math.floor((min + max) / 2);
}

/**
 * Generates the narrative structure and text of the story.
 */
export async function generateStoryContent(config: StoryConfig, childProfile: ChildProfile): Promise<GeneratedBook> {
  debugLog("GENAI", "generateStoryContent: Start", { prompt: config.prompt, child: childProfile.name });

  const pageCount = calculatePageCount(config.targetAudience, config.length);
  const selectedVoice = AVAILABLE_VOICES[Math.floor(Math.random() * AVAILABLE_VOICES.length)];

  const isOlder = config.targetAudience.includes('Big') || childProfile.age >= 8;

  const systemInstruction = `You are a world-class children's author specialized in high-quality picture books. 
    Create a personalized story for ${childProfile.name} (Age ${childProfile.age}).
    
    CONTENT RULES:
    1. Base it on: "${config.prompt}".
    2. Traits: ${childProfile.appearance}.
    3. LANGUAGE STYLE: ${isOlder
      ? "Rich, descriptive language with slightly longer paragraphs and deeper emotional beats suitable for ages 8-10. Use more sophisticated sentence structures while maintaining readability."
      : "Simple, engaging language with clear narrative arcs. For toddlers, keep paragraphs very short and rhythmic."
    }
    4. Tone: ${config.tone}.
    5. SAFETY: No violence. Happy ending.
    
    CHARACTER BIBLE RULES (CRITICAL FOR CONSISTENCY):
    1. Create a DETAILED characterBible with main character and any supporting characters.
    2. For each character, define:
       - type: "animal", "human", or "fantasy"
       - species and breed (if applicable, e.g., "golden retriever" for a dog)
       - appearance: skin/coat color, color palette, distinctive marks, clothing/accessories
       - personality traits as an array
       - do_not_change: list of traits that MUST remain constant (e.g., "always wears red cape", "has blue scales")
    3. Define style_lock with art_style matching "${config.illustrationStyle}", camera_rules (e.g., "always show full body"), and consistency_rules.
    4. NEVER change a character's species, breed, or core appearance across pages.
    5. EVERY imagePrompt MUST explicitly restate: character name, species/breed, distinctive marks, and key accessories from the characterBible. Do not assume the illustrator remembers - repeat locked traits every time.
    6. If type is "animal", NEVER use words like "boy", "girl", "person", or "human" in any imagePrompt.
    
    FORMATTING RULES:
    1. EVERY page MUST have exactly one "text" block and one "imagePrompt".
    2. NO text-only or image-only pages.
    3. The text should be suitable for reading aloud and match the illustration described in the prompt.
    4. NEVER include page headers or titles within the page text.`;

  const promptText = `Create a ${pageCount}-page children's book about: ${config.prompt}. Provide a title, synopsis, character description, and ${pageCount} pages. Each page must have an evocative image prompt and corresponding text.`;

  try {
    const data = await callGeminiProxy({
      model: 'gemini-3-pro-preview',
      contents: [{ parts: [{ text: promptText }] }],
      config: {
        systemInstruction,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING },
            synopsis: { type: Type.STRING },
            characterBible: {
              type: Type.OBJECT,
              properties: {
                main: {
                  type: Type.OBJECT,
                  properties: {
                    name: { type: Type.STRING },
                    type: { type: Type.STRING },
                    species: { type: Type.STRING },
                    breed: { type: Type.STRING },
                    age: { type: Type.STRING },
                    appearance: {
                      type: Type.OBJECT,
                      properties: {
                        coat_or_skin: { type: Type.STRING },
                        color_palette: { type: Type.STRING },
                        distinctive_marks: { type: Type.ARRAY, items: { type: Type.STRING } },
                        clothing_or_accessories: { type: Type.ARRAY, items: { type: Type.STRING } },
                      },
                      required: ["coat_or_skin", "color_palette", "distinctive_marks", "clothing_or_accessories"],
                    },
                    personality: { type: Type.ARRAY, items: { type: Type.STRING } },
                    do_not_change: { type: Type.ARRAY, items: { type: Type.STRING } },
                  },
                  required: ["name", "type", "appearance", "personality", "do_not_change"],
                },
                supporting: {
                  type: Type.ARRAY,
                  items: {
                    type: Type.OBJECT,
                    properties: {
                      name: { type: Type.STRING },
                      type: { type: Type.STRING },
                      species: { type: Type.STRING },
                      breed: { type: Type.STRING },
                      age: { type: Type.STRING },
                      appearance: {
                        type: Type.OBJECT,
                        properties: {
                          coat_or_skin: { type: Type.STRING },
                          color_palette: { type: Type.STRING },
                          distinctive_marks: { type: Type.ARRAY, items: { type: Type.STRING } },
                          clothing_or_accessories: { type: Type.ARRAY, items: { type: Type.STRING } },
                        },
                        required: ["coat_or_skin", "color_palette", "distinctive_marks", "clothing_or_accessories"],
                      },
                      personality: { type: Type.ARRAY, items: { type: Type.STRING } },
                      do_not_change: { type: Type.ARRAY, items: { type: Type.STRING } },
                    },
                    required: ["name", "type", "appearance", "personality", "do_not_change"],
                  },
                },
                style_lock: {
                  type: Type.OBJECT,
                  properties: {
                    art_style: { type: Type.STRING },
                    camera_rules: { type: Type.ARRAY, items: { type: Type.STRING } },
                    consistency_rules: { type: Type.ARRAY, items: { type: Type.STRING } },
                  },
                  required: ["art_style", "camera_rules", "consistency_rules"],
                },
              },
              required: ["main", "supporting", "style_lock"],
            },
            pages: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  pageNumber: { type: Type.INTEGER },
                  text: { type: Type.STRING },
                  imagePrompt: { type: Type.STRING },
                  layout: { type: Type.STRING },
                },
                required: ["pageNumber", "text", "imagePrompt", "layout"],
              },
            },
            estimatedReadingTime: { type: Type.STRING },
            tags: { type: Type.ARRAY, items: { type: Type.STRING } },
          },
          required: ["title", "synopsis", "characterBible", "pages", "estimatedReadingTime", "tags"]
        }
      }
    });

    const storyData = JSON.parse(data.text || '{}');
    if (!storyData.pages) throw new Error("INVALID_STORY_STRUCTURE");

    // Log Character Bible for debugging consistency
    const characterBible = storyData.characterBible as CharacterBible;
    await debugLogCharacterBible(characterBible);

    debugLog("GENAI", "generateStoryContent: Success", { title: storyData.title });

    return {
      ...storyData,
      illustrationStyle: config.illustrationStyle,
      authorName: childProfile.authorName || childProfile.name,
      voiceName: selectedVoice,
      coverImagePrompt: `A magnificent, high-quality children's book cover illustration for a story titled "${storyData.title}". Style: ${config.illustrationStyle}. The scene features ${childProfile.name} (${childProfile.appearance}) in the setting described: ${storyData.synopsis}. Cinematic lighting, vibrant colors, magical atmosphere. NO TEXT ON IMAGE.`,
    };
  } catch (err: any) {
    console.error("Story content generation error:", err);
    throw err;
  }
}

export async function generateBookAssets(book: GeneratedBook, childProfile?: ChildProfile, onProgress?: (c: number, t: number) => void): Promise<GeneratedBook> {
  debugLog("GENAI", "generateBookAssets: Start", { pages: book.pages.length, voice: book.voiceName });
  const totalAssets = 1 + (book.pages.length * 2);
  let completedCount = 0;
  const lockedVoiceName = (book.voiceName || 'Kore').trim();

  const increment = () => {
    completedCount++;
    if (onProgress) onProgress(completedCount, totalAssets);
  };

  // Build Identity Lock Header from CharacterBible for consistent character representation
  const identityLockHeader = buildIdentityLockHeader(book.characterBible);
  const mainCharacterType = book.characterBible.main.type;
  const hasReferenceImage = !!childProfile?.photoBase64;

  const enhancePrompt = (basePrompt: string, context: string) => {
    let enhancedPrompt = `${identityLockHeader}\n\n${basePrompt}.`;

    // Only mention "person" and "facial features" for human characters with reference images
    if (mainCharacterType === 'human' && hasReferenceImage) {
      enhancedPrompt += `\nIMPORTANT: The main character MUST look exactly like the person in the provided reference image.\nMaintain consistent facial features, hair, and clothing.`;
    }

    // Add FORBIDDEN list for animal characters to prevent anthropomorphization
    if (mainCharacterType === 'animal') {
      enhancedPrompt += `\nFORBIDDEN: human, person, boy, girl, man, woman, humanoid, anthropomorphic. The main character is an animal and must remain an animal.`;
    }

    enhancedPrompt += `\nStyle: ${book.illustrationStyle}. NO TEXT ON IMAGE.`;

    // Runtime assertion to ensure Identity Lock Header is present
    assertIdentityHeaderPresent(enhancedPrompt, context);

    return enhancedPrompt;
  };

  const coverImageUrl = await retryOperation(() =>
    generateImage(enhancePrompt(book.coverImagePrompt, 'cover image prompt'), childProfile?.photoBase64)
  )
    .then(url => { increment(); return url; })
    .catch((e) => {
      debugLog("GENAI", "Cover Image Failed", e.message);
      increment(); return '';
    });

  const updatedPages = [...book.pages];
  const CONCURRENCY_LIMIT = 4;
  const pool = new Set<Promise<void>>();
  const tasks: (() => Promise<void>)[] = [];

  updatedPages.forEach((page, index) => {
    tasks.push(async () => {
      try {
        const prompt = `Page ${page.pageNumber}: ${page.imagePrompt}`;
        updatedPages[index].imageUrl = await retryOperation(() =>
          generateImage(enhancePrompt(prompt, `page ${page.pageNumber} image prompt`), childProfile?.photoBase64)
        );
      } catch (e: any) {
        debugLog("GENAI", `Image Gen Failed Page ${index}`, e.message);
        updatedPages[index].imageUrl = '';
      } finally {
        increment();
      }
    });

    tasks.push(async () => {
      try {
        updatedPages[index].audioBase64 = await retryOperation(() => generateSpeech(page.text, lockedVoiceName));
      } catch (e: any) {
        debugLog("GENAI", `Audio Gen Failed Page ${index}`, e.message);
        updatedPages[index].audioBase64 = '';
      } finally {
        increment();
      }
    });
  });

  for (const task of tasks) {
    const promise = task().then(() => {
      pool.delete(promise);
    });
    pool.add(promise);
    if (pool.size >= CONCURRENCY_LIMIT) {
      await Promise.race(pool);
    }
  }

  await Promise.all(pool);
  debugLog("GENAI", "generateBookAssets: Complete");
  return { ...book, coverImageUrl, pages: updatedPages, voiceName: lockedVoiceName };
}

export async function generateImage(promptText: string, referenceImageBase64?: string): Promise<string> {
  const parts: any[] = [];

  if (referenceImageBase64) {
    const matches = referenceImageBase64.match(/^data:(.+);base64,(.+)$/);
    if (matches) {
      parts.push({
        inlineData: {
          mimeType: matches[1],
          data: matches[2]
        }
      });
    }
  }

  parts.push({ text: promptText });

  const data = await callGeminiProxy({
    model: 'gemini-2.5-flash-image',
    contents: { parts }
  });

  const part = data.candidates?.[0]?.content?.parts?.find((p: any) => p.inlineData);
  if (part?.inlineData) {
    return `data:image/png;base64,${part.inlineData.data}`;
  }

  if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
    debugLog("GENAI", "Image Refusal", data.candidates[0].content.parts[0].text);
    throw new Error("AI refused to generate image (Safety/Policy).");
  }

  throw new Error("NO_IMAGE_DATA");
}

export async function generateSpeech(text: string, voiceName: string): Promise<string> {
  const lockedVoiceName = (voiceName || '').trim();
  if (!lockedVoiceName) throw new Error("MISSING_VOICE_NAME");

  const data = await callGeminiProxy({
    model: "gemini-2.5-flash-preview-tts",
    contents: [{ parts: [{ text }] }],
    config: {
      responseModalities: [Modality.AUDIO],
      speechConfig: {
        voiceConfig: {
          prebuiltVoiceConfig: { voiceName: lockedVoiceName }
        }
      }
    }
  });

  const audioData = data.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
  if (!audioData) throw new Error("NO_AUDIO_DATA");
  return audioData;
}
